import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Eye, EyeOff, Loader2, ArrowLeft, Check } from 'lucide-react';
import { GoogleAuthButton } from '../components/GoogleAuthButton';

const RegisterPage = () => {
  const navigate = useNavigate();
  const { register } = useAuth();
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    first_name: '',
    last_name: '',
    phone: '',
    outreach_audience: '',  // '' | 'attorney' | 'funeral_home' | 'cpa'
    organization: '',
    practice_focus: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    const { name, type, value, checked } = e.target;
    setFormData({ ...formData, [name]: type === 'checkbox' ? checked : value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (formData.password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }

    setLoading(true);

    try {
      const { confirmPassword, ...userData } = formData;
      await register(userData);
      navigate('/dashboard');
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to create account. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const benefits = [
    'Free 30-day trial',
    'No credit card required',
    'Manage unlimited estates',
    'Court-ready PDF reports',
  ];

  return (
    <div className="min-h-screen bg-ep-background flex" data-testid="register-page">
      {/* Left Panel - Branding */}
      <div className="hidden lg:flex lg:w-1/2 bg-ep-primary p-12 flex-col justify-between">
        <div>
          <Link to="/" className="inline-flex items-center gap-1 mb-12">
            <span className="font-heading text-2xl font-bold text-white">Estate</span>
            <span className="font-heading text-2xl font-bold text-ep-secondary">Pro</span>
            <span className="font-heading text-2xl font-bold text-white">Filer</span>
          </Link>
          
          <h1 className="font-heading text-4xl font-bold text-white leading-tight mb-6">
            Start managing your<br />
            <span className="italic text-ep-secondary">estate today</span>
          </h1>
          <p className="text-white/70 text-lg mb-8">
            Simplify estate administration with guided task management and professional reports.
          </p>

          <div className="space-y-4">
            {benefits.map((benefit, index) => (
              <div key={index} className="flex items-center gap-3">
                <div className="w-6 h-6 rounded-full bg-ep-secondary/20 flex items-center justify-center">
                  <Check className="w-4 h-4 text-ep-secondary" strokeWidth={2.5} />
                </div>
                <span className="text-white/90">{benefit}</span>
              </div>
            ))}
          </div>
        </div>
        
        <div className="bg-white/10 rounded-xl p-6">
          <p className="text-white/90 mb-3">
            <span className="text-ep-secondary font-semibold">Estate administration made simple.</span> Our step-by-step guidance helps you navigate the probate process with confidence.
          </p>
          <p className="text-white/60 text-sm">All 50 states supported</p>
        </div>
      </div>

      {/* Right Panel - Register Form */}
      <div className="flex-1 flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          {/* Mobile Logo */}
          <div className="lg:hidden mb-8 text-center">
            <Link to="/" className="inline-flex items-center gap-1">
              <span className="font-heading text-2xl font-bold text-ep-primary">Estate</span>
              <span className="font-heading text-2xl font-bold text-ep-secondary">Pro</span>
              <span className="font-heading text-2xl font-bold text-ep-primary">Filer</span>
            </Link>
          </div>

          {/* Back to Home */}
          <Link 
            to="/" 
            className="inline-flex items-center gap-2 text-ep-text-muted hover:text-ep-text mb-8 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            <span className="text-sm">Back to home</span>
          </Link>

          <div className="mb-8">
            <h2 className="font-heading text-3xl font-bold text-ep-text mb-2">Create your account</h2>
            <p className="text-ep-text-secondary">Start your free trial — no credit card required</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            {error && (
              <div className="bg-red-50 text-red-700 p-4 rounded-xl text-sm border border-red-200">
                {error}
              </div>
            )}

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="first_name" className="text-ep-text">First name</Label>
                <Input
                  id="first_name"
                  name="first_name"
                  placeholder="John"
                  value={formData.first_name}
                  onChange={handleChange}
                  required
                  className="h-12 bg-white border-ep-border focus:border-ep-primary focus:ring-ep-primary/20"
                  data-testid="register-first-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="last_name" className="text-ep-text">Last name</Label>
                <Input
                  id="last_name"
                  name="last_name"
                  placeholder="Doe"
                  value={formData.last_name}
                  onChange={handleChange}
                  required
                  className="h-12 bg-white border-ep-border focus:border-ep-primary focus:ring-ep-primary/20"
                  data-testid="register-last-name-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="email" className="text-ep-text">Email address</Label>
              <Input
                id="email"
                name="email"
                type="email"
                placeholder="you@example.com"
                value={formData.email}
                onChange={handleChange}
                required
                className="h-12 bg-white border-ep-border focus:border-ep-primary focus:ring-ep-primary/20"
                data-testid="register-email-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="phone" className="text-ep-text">Phone <span className="text-ep-text-muted">(optional)</span></Label>
              <Input
                id="phone"
                name="phone"
                type="tel"
                placeholder="(555) 123-4567"
                value={formData.phone}
                onChange={handleChange}
                className="h-12 bg-white border-ep-border focus:border-ep-primary focus:ring-ep-primary/20"
                data-testid="register-phone-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="text-ep-text">Password</Label>
              <div className="relative">
                <Input
                  id="password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="••••••••"
                  value={formData.password}
                  onChange={handleChange}
                  required
                  className="h-12 bg-white border-ep-border focus:border-ep-primary focus:ring-ep-primary/20 pr-12"
                  data-testid="register-password-input"
                />
                <button
                  type="button"
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-ep-text-muted hover:text-ep-text transition-colors"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
              <p className="text-xs text-ep-text-muted">Must be at least 6 characters</p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="confirmPassword" className="text-ep-text">Confirm password</Label>
              <Input
                id="confirmPassword"
                name="confirmPassword"
                type={showPassword ? 'text' : 'password'}
                placeholder="••••••••"
                value={formData.confirmPassword}
                onChange={handleChange}
                required
                className="h-12 bg-white border-ep-border focus:border-ep-primary focus:ring-ep-primary/20"
                data-testid="register-confirm-password-input"
              />
            </div>

            <div className="border-t border-ep-border pt-4">
              <Label className="text-sm font-medium text-ep-text">I'm in this profession (optional)</Label>
              <p className="text-xs text-ep-text-muted mt-0.5 mb-3">Receive our free Executor Success Kit and occasional updates relevant to your work.</p>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2" data-testid="register-audience-picker">
                {[
                  { id: 'attorney', label: 'Attorney' },
                  { id: 'funeral_home', label: 'Funeral home' },
                  { id: 'cpa', label: 'CPA / accountant' },
                  { id: 'probate_court', label: 'Probate court staff' },
                ].map((o) => (
                  <button
                    key={o.id}
                    type="button"
                    onClick={() => setFormData((f) => ({
                      ...f,
                      outreach_audience: f.outreach_audience === o.id ? '' : o.id,
                    }))}
                    data-testid={`register-audience-${o.id}`}
                    className={`text-sm px-3 py-2 rounded-lg border transition-colors ${formData.outreach_audience === o.id ? 'bg-emerald-900 text-white border-emerald-900' : 'bg-white text-ep-text border-ep-border hover:border-emerald-700'}`}
                  >
                    {o.label}
                  </button>
                ))}
              </div>

              {formData.outreach_audience && (
                <div className="grid sm:grid-cols-2 gap-3 mt-3" data-testid="register-audience-fields">
                  <div>
                    <Label htmlFor="organization" className="text-ep-text text-xs">
                      {formData.outreach_audience === 'funeral_home' ? 'Funeral home name' :
                        formData.outreach_audience === 'probate_court' ? 'Court / office name' :
                        formData.outreach_audience === 'cpa' ? 'Firm name' : 'Firm name'} (optional)
                    </Label>
                    <Input
                      id="organization"
                      name="organization"
                      value={formData.organization}
                      onChange={handleChange}
                      placeholder={formData.outreach_audience === 'probate_court' ? 'e.g. Greenville County Probate Court' : 'e.g. Oak Hill Funeral Home'}
                      className="h-10 bg-white border-ep-border"
                      data-testid="register-organization-input"
                    />
                  </div>
                  {formData.outreach_audience === 'attorney' && (
                    <div>
                      <Label htmlFor="practice_focus" className="text-ep-text text-xs">Practice focus</Label>
                      <Input
                        id="practice_focus"
                        name="practice_focus"
                        value={formData.practice_focus}
                        onChange={handleChange}
                        placeholder="probate, estate planning"
                        className="h-10 bg-white border-ep-border"
                        data-testid="register-practice-focus-input"
                      />
                    </div>
                  )}
                </div>
              )}
            </div>

            <Button 
              type="submit" 
              className="w-full h-12 bg-ep-primary hover:bg-ep-primary-dark text-white font-medium"
              disabled={loading}
              data-testid="register-submit-btn"
            >
              {loading ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Creating account...</>
              ) : (
                'Create Account'
              )}
            </Button>

            <p className="text-xs text-ep-text-muted text-center">
              By creating an account, you agree to our{' '}
              <Link to="/terms" className="text-ep-primary hover:underline">Terms of Service</Link>
              {' '}and{' '}
              <Link to="/privacy" className="text-ep-primary hover:underline">Privacy Policy</Link>
            </p>
          </form>

          <div className="mt-6 relative">
            <div className="absolute inset-0 flex items-center" aria-hidden="true">
              <div className="w-full border-t border-ep-border/60"></div>
            </div>
            <div className="relative flex justify-center text-xs uppercase">
              <span className="bg-ep-background px-2 text-ep-text-muted tracking-wider">or</span>
            </div>
          </div>

          <div className="mt-6">
            <GoogleAuthButton label="Sign up with Google" disabled={loading} testId="google-register-btn" />
          </div>

          <p className="mt-8 text-center text-ep-text-secondary">
            Already have an account?{' '}
            <Link to="/login" className="text-ep-primary hover:underline font-medium">
              Sign in
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default RegisterPage;
